<template>
  <div class="pre-matricula-page">
    <v-container fluid class="pa-0">
      <div class="page-header">
        <div class="header-content">
          <v-icon color="white" size="48" class="mb-2">mdi-school</v-icon>
          <h1 class="white--text text-h5 text-sm-h4 font-weight-bold mb-2">Pré-Matrícula Online</h1>
          <p class="white--text text-body-2 text-sm-body-1 mb-0">
            Realize sua pré-matrícula de forma rápida e fácil
          </p>
        </div>
      </div>

      <v-container class="form-container py-4 py-sm-8">
        <v-card class="form-card elevation-8" max-width="900">
          <div class="stepper-container pa-4 pa-sm-6">
            <div class="stepper-wrapper">
              <div class="step-item" :class="{ active: etapa >= 1, completed: etapa > 1 }">
                <div class="step-circle">
                  <v-icon v-if="etapa > 1" color="white" size="20">mdi-check</v-icon>
                  <span v-else class="step-number">1</span>
                </div>
                <span class="step-label">CPF</span>
              </div>
              <div class="step-line" :class="{ active: etapa > 1 }"></div>
              <div class="step-item" :class="{ active: etapa >= 2 }">
                <div class="step-circle">
                  <span class="step-number">2</span>
                </div>
                <span class="step-label">Dados</span>
              </div>
            </div>
          </div>

          <v-divider></v-divider>

          <v-card-text class="pa-4 pa-sm-8">
            <ValidationObserver v-slot="{ handleSubmit }">
              <form @submit.prevent="handleSubmit(submitForm)">
                <v-expand-transition>
                  <div v-if="etapa === 1" class="etapa-content">
                    <div class="text-center mb-6">
                      <v-icon color="primary" size="64" class="mb-3">mdi-account-search</v-icon>
                      <h2 class="text-h6 text-sm-h5 mb-2 primary--text">Vamos começar!</h2>
                      <p class="text-body-2 text-sm-body-1 grey--text text--darken-1">
                        Informe o CPF do aluno para continuar
                      </p>
                    </div>

                    <v-row justify="center">
                      <v-col cols="12" sm="10" md="8">
                        <ValidationProvider name="CPF" rules="required" v-slot="{ errors }">
                          <e-label class="field-label">
                            <v-icon small class="mr-1">mdi-card-account-details</v-icon>
                            CPF do Aluno
                          </e-label>
                          <v-text-field
                            v-model="form.cpf"
                            v-mask="'###.###.###-##'"
                            :error-messages="errors"
                            solo
                            dense
                            placeholder="000.000.000-00"
                            class="cpf-input"
                            hide-details="auto"
                            prepend-inner-icon="mdi-card-account-details"
                            @keyup.enter="cpfValido && buscarAluno()"
                          />
                        </ValidationProvider>
                      </v-col>
                    </v-row>

                    <div class="d-flex justify-center align-center mt-6" style="gap: 12px">
                      <v-btn
                        color="grey darken-1"
                        text
                        @click="$router.push({ name: 'login' })"
                        class="text-none"
                        large
                      >
                        <v-icon left>mdi-arrow-left</v-icon>
                        Voltar
                      </v-btn>
                      <v-btn
                        color="primary"
                        @click="buscarAluno"
                        :loading="loading"
                        :disabled="!cpfValido"
                        x-large
                        rounded
                        elevation="2"
                        min-width="200"
                        class="text-none font-weight-bold"
                      >
                        <v-icon left>mdi-arrow-right</v-icon>
                        Continuar
                      </v-btn>
                    </div>
                  </div>
                </v-expand-transition>

                <v-expand-transition>
                  <div v-if="etapa === 2" class="etapa-content">
                    <v-alert
                      v-if="possuiMatriculaAtiva"
                      type="warning"
                      prominent
                      border="left"
                      colored-border
                      icon="mdi-alert-circle"
                      class="mb-6 mobile-alert"
                    >
                      <div class="text-body-2">
                        <strong>Matrícula já existente!</strong><br />
                        O aluno <strong>{{ aluno.nomecompleto }}</strong> já possui uma matrícula
                        ativa para este ano. <br /><br />
                        Não é possível criar uma pré-matrícula para alunos que já estão matriculados
                        no ano letivo atual.
                      </div>
                    </v-alert>

                    <v-alert
                      v-if="!alunoExiste && !possuiMatriculaAtiva"
                      type="info"
                      text
                      border="left"
                      colored-border
                      icon="mdi-information"
                      class="mb-6 mobile-alert"
                    >
                      <div class="text-body-2">
                        <strong>Aluno não encontrado</strong><br />
                        Por favor, preencha os dados abaixo para continuar.
                      </div>
                    </v-alert>

                    <v-alert
                      v-if="alunoExiste && !possuiMatriculaAtiva"
                      type="success"
                      text
                      border="left"
                      colored-border
                      icon="mdi-check-circle"
                      class="mb-6 mobile-alert"
                    >
                      <div class="text-body-2">
                        <strong>Aluno encontrado!</strong><br />
                        {{ aluno.nomecompleto }}
                      </div>
                    </v-alert>

                    <template v-if="!alunoExiste && !possuiMatriculaAtiva">
                      <div class="section-header mb-4">
                        <v-icon color="primary" class="mr-2">mdi-account</v-icon>
                        <h3 class="text-h6 primary--text mb-0">Dados do Aluno</h3>
                      </div>

                      <v-row>
                        <v-col cols="12">
                          <ValidationProvider name="Nome" rules="required" v-slot="{ errors }">
                            <e-label class="field-label">Nome Completo</e-label>
                            <v-text-field
                              v-model="form.nome"
                              :error-messages="errors"
                              solo
                              dense
                              hide-details="auto"
                              placeholder="Digite o nome completo"
                            />
                          </ValidationProvider>
                        </v-col>

                        <v-col cols="12" sm="6">
                          <ValidationProvider
                            name="Data de Nascimento"
                            rules="required"
                            v-slot="{ errors }"
                          >
                            <e-label class="field-label">Data de Nascimento</e-label>
                            <e-date-picker
                              v-model="form.data_nascimento"
                              :error-messages="errors"
                              placeholder="dd/mm/yyyy"
                            />
                          </ValidationProvider>
                        </v-col>

                        <v-col cols="12" sm="6">
                          <ValidationProvider name="Sexo" rules="required" v-slot="{ errors }">
                            <e-label class="field-label">Gênero</e-label>
                            <v-select
                              v-model="form.sexo_id"
                              :items="sexos"
                              item-text="descricao"
                              item-value="id"
                              :error-messages="errors"
                              solo
                              dense
                              hide-details="auto"
                            />
                          </ValidationProvider>
                        </v-col>

                        <v-col cols="12" sm="6">
                          <ValidationProvider
                            name="Filiação 1"
                            rules="required"
                            v-slot="{ errors }"
                          >
                            <e-label class="field-label">Filiação 1 (Obrigatório)</e-label>
                            <v-text-field
                              v-model="form.filiacao_1"
                              :error-messages="errors"
                              solo
                              dense
                              hide-details="auto"
                              placeholder="Nome do pai ou mãe"
                            />
                          </ValidationProvider>
                        </v-col>

                        <v-col cols="12" sm="6">
                          <e-label class="field-label">Filiação 2 (Opcional)</e-label>
                          <v-text-field
                            v-model="form.filiacao_2"
                            solo
                            dense
                            hide-details="auto"
                            placeholder="Nome do pai ou mãe"
                          />
                        </v-col>

                        <v-col cols="12" sm="6">
                          <ValidationProvider name="Cor/Raça" rules="required" v-slot="{ errors }">
                            <e-label class="field-label">Cor/Raça</e-label>
                            <v-select
                              v-model="form.cor_raca"
                              :items="cores"
                              :error-messages="errors"
                              solo
                              dense
                              hide-details="auto"
                            />
                          </ValidationProvider>
                        </v-col>

                        <v-col cols="12" sm="6">
                          <ValidationProvider name="País" rules="required" v-slot="{ errors }">
                            <e-label class="field-label">País de Nacionalidade</e-label>
                            <v-text-field
                              v-model="form.pais"
                              :error-messages="errors"
                              solo
                              dense
                              hide-details="auto"
                            />
                          </ValidationProvider>
                        </v-col>

                        <v-col cols="12" sm="6">
                          <ValidationProvider
                            name="UF de Nascimento"
                            rules="required"
                            v-slot="{ errors }"
                          >
                            <e-label class="field-label">UF de Nascimento</e-label>
                            <v-select
                              v-model="form.uf_id"
                              :items="estados"
                              item-text="descricao"
                              item-value="id"
                              :error-messages="errors"
                              solo
                              dense
                              hide-details="auto"
                              @change="onEstadoChange"
                            />
                          </ValidationProvider>
                        </v-col>

                        <v-col cols="12" sm="6">
                          <ValidationProvider
                            name="Município de Nascimento"
                            rules="required"
                            v-slot="{ errors }"
                          >
                            <e-label class="field-label">Município de Nascimento</e-label>
                            <v-select
                              v-model="form.cidade_nascimento"
                              :items="cidades"
                              :loading="loadingCidades"
                              :disabled="!form.uf_id"
                              :error-messages="errors"
                              solo
                              dense
                              hide-details="auto"
                            />
                          </ValidationProvider>
                        </v-col>
                      </v-row>
                    </template>

                    <template v-if="!possuiMatriculaAtiva">
                      <v-divider class="my-6"></v-divider>

                      <div class="section-header mb-4">
                        <v-icon color="primary" class="mr-2">mdi-school</v-icon>
                        <h3 class="text-h6 primary--text mb-0">Dados da Matrícula</h3>
                      </div>

                      <v-row>
                        <v-col cols="12" sm="4">
                          <ValidationProvider name="Escola" rules="required" v-slot="{ errors }">
                            <e-label class="field-label">Escola</e-label>
                            <e-autocomplete
                              v-model="form.configuracao_id"
                              :items="escolas"
                              item-text="descricao"
                              item-value="id"
                              :error-messages="errors"
                              solo
                              dense
                              hide-details="auto"
                              @change="onEscolaChange"
                            />
                          </ValidationProvider>
                        </v-col>

                        <v-col cols="12" sm="4">
                          <ValidationProvider name="Turma" rules="required" v-slot="{ errors }">
                            <e-label class="field-label">Turma</e-label>
                            <e-autocomplete
                              v-model="form.turma_id"
                              :items="turmasFiltradas"
                              item-text="descricao"
                              item-value="id"
                              :error-messages="errors"
                              :disabled="!form.configuracao_id"
                              solo
                              dense
                              hide-details="auto"
                              @change="onTurmaChange"
                            />

                            <v-alert
                              v-if="turmaAtualInfo && turmaAtualInfo.limite_prematricula"
                              :type="turmaAtualInfo.limite_atingido ? 'error' : 'info'"
                              dense
                              text
                              class="mt-2"
                            >
                              <template v-if="turmaAtualInfo.limite_atingido">
                                <!-- <v-icon small color="error" class="mr-1">mdi-alert-circle</v-icon> -->
                                Turma lotada - selecione outra turma
                              </template>
                              <template v-else-if="turmaAtualInfo.vagas_disponiveis <= 5">
                                <!-- <v-icon small color="warning" class="mr-1">mdi-alert</v-icon> -->
                                Apenas {{ turmaAtualInfo.vagas_disponiveis }} vaga(s) restante(s)
                              </template>
                              <template v-else>
                                <!-- <v-icon small color="info" class="mr-1">mdi-information</v-icon> -->
                                {{ turmaAtualInfo.vagas_disponiveis }} vagas disponíveis
                              </template>
                            </v-alert>
                          </ValidationProvider>
                        </v-col>

                        <v-col cols="12" sm="4">
                          <ValidationProvider name="Etapa" rules="required" v-slot="{ errors }">
                            <e-label class="field-label">Etapa</e-label>
                            <e-autocomplete
                              v-model="form.serie_id"
                              :items="seriesFiltradas"
                              item-text="descricao"
                              item-value="id"
                              :error-messages="errors"
                              :disabled="!form.turma_id"
                              solo
                              dense
                              hide-details="auto"
                            />
                          </ValidationProvider>
                        </v-col>
                      </v-row>
                    </template>

                    <div class="form-actions mt-8">
                      <v-btn
                        color="grey darken-1"
                        text
                        @click="voltarEtapa"
                        class="text-none"
                        large
                      >
                        <v-icon left>mdi-arrow-left</v-icon>
                        Voltar
                      </v-btn>
                      <v-btn
                        v-if="!possuiMatriculaAtiva"
                        color="success"
                        type="submit"
                        :loading="loading"
                        :disabled="!formularioValido"
                        class="text-none font-weight-bold ml-2"
                        large
                        rounded
                        elevation="2"
                      >
                        <v-icon left>mdi-check</v-icon>
                        Salvar Pré-Matrícula
                      </v-btn>
                    </div>
                  </div>
                </v-expand-transition>
              </form>
            </ValidationObserver>
          </v-card-text>
        </v-card>
      </v-container>
    </v-container>
  </div>
</template>

<script>
export default {
  name: "PreMatriculaFormPage",
  data() {
    return {
      etapa: 1,
      loading: false,
      loadingCidades: false,
      alunoExiste: false,
      possuiMatriculaAtiva: false,
      aluno: null,
      form: {
        cpf: "",
        aluno_id: null,
        nome: "",
        data_nascimento: "",
        filiacao_1: "",
        filiacao_2: "",
        sexo_id: null,
        cor_raca: "",
        pais: "Brasil",
        uf_id: null,
        cidade_nascimento: "",
        configuracao_id: null,
        turma_id: null,
        serie_id: null,
      },
      escolas: [],
      sexos: [],
      estados: [],
      cidades: [],
      cores: ["Branca", "Preta", "Parda", "Amarela", "Indígena"],
      todasTurmas: [],
      todasSeries: [],
      cpfValidationCache: new Map(),
      cidadesCache: {},
      turmasPorConfiguracao: {},
      fetchTimeout: null,
      abortController: null,
    };
  },
  computed: {
    cpfValido() {
      if (!this.form.cpf) {
        return false;
      }

      if (this.cpfValidationCache.has(this.form.cpf)) {
        return this.cpfValidationCache.get(this.form.cpf);
      }

      const result = this.validarCpf(this.form.cpf);
      this.cpfValidationCache.set(this.form.cpf, result);
      return result;
    },
    formularioValido() {
      // Se o aluno possui matrícula ativa, o formulário não é válido
      if (this.possuiMatriculaAtiva) {
        return false;
      }
      if (this.turmaAtualAtingiuLimite) {
        return false;
      }

      const camposMatriculaValidos =
        this.form.configuracao_id && this.form.turma_id && this.form.serie_id;

      if (this.alunoExiste) {
        return camposMatriculaValidos;
      }
      const camposAlunoValidos =
        this.form.nome &&
        this.form.data_nascimento &&
        this.form.sexo_id &&
        this.form.filiacao_1 &&
        this.form.cor_raca &&
        this.form.pais &&
        this.form.uf_id &&
        this.form.cidade_nascimento;

      return camposMatriculaValidos && camposAlunoValidos;
    },
    turmasFiltradas() {
      if (!this.form.configuracao_id) {
        return [];
      }
      const turmas = this.turmasPorConfiguracao[this.form.configuracao_id] || [];

      return turmas.map((turma) => ({
        ...turma,
        descricao: this.formatarDescricaoTurma(turma),
      }));
    },
    turmaAtualInfo() {
      if (!this.form.turma_id) return null;
      return this.todasTurmas.find((t) => t.id === this.form.turma_id);
    },
    turmaAtualAtingiuLimite() {
      return this.turmaAtualInfo?.limite_atingido || false;
    },
    seriesFiltradas() {
      return this.todasSeries;
    },
    estadoSelecionado() {
      return this.estados.find((e) => e.id === this.form.uf_id);
    },
  },
  methods: {
    validarCpf(cpf) {
      const cpfLimpo = cpf.replace(/[^\d]/g, "");

      if (cpfLimpo.length !== 11) {
        return false;
      }

      if (/^(\d)\1{10}$/.test(cpfLimpo)) {
        return false;
      }

      let soma = 0;
      for (let i = 0; i < 9; i++) {
        soma += parseInt(cpfLimpo.charAt(i), 10) * (10 - i);
      }
      let resto = soma % 11;
      const digito1 = resto < 2 ? 0 : 11 - resto;

      if (digito1 !== parseInt(cpfLimpo.charAt(9), 10)) {
        return false;
      }

      soma = 0;
      for (let i = 0; i < 10; i++) {
        soma += parseInt(cpfLimpo.charAt(i), 10) * (11 - i);
      }
      resto = soma % 11;
      const digito2 = resto < 2 ? 0 : 11 - resto;

      if (digito2 !== parseInt(cpfLimpo.charAt(10), 10)) {
        return false;
      }

      return true;
    },
    async buscarAluno() {
      if (!this.form.cpf) {
        this.$toast.danger("Por favor, informe o CPF");
        return;
      }

      if (!this.validarCpf(this.form.cpf)) {
        this.$toast.danger("CPF inválido");
        return;
      }

      this.loading = true;
      try {
        const response = await this.$services.preMatriculasService.buscarPorCpf(this.form.cpf);

        if (response.existe) {
          this.alunoExiste = true;
          this.aluno = response.aluno;
          this.form.aluno_id = response.aluno.id;
          this.possuiMatriculaAtiva = response.possui_matricula_ativa;
        } else {
          this.alunoExiste = false;
          this.possuiMatriculaAtiva = false;
        }
        const turmas = await this.$services.preMatriculasService.listarTurmasPublico();
        this.todasTurmas = turmas;
        this.turmasPorConfiguracao = turmas.reduce((acc, turma) => {
          if (!acc[turma.configuracao_id]) {
            acc[turma.configuracao_id] = [];
          }
          acc[turma.configuracao_id].push(turma);
          return acc;
        }, {});

        this.etapa = 2;
      } catch (error) {
        this.$toast.danger(error.message || "Erro ao buscar aluno");
      } finally {
        this.loading = false;
      }
    },
    async submitForm() {
      if (this.turmaAtualAtingiuLimite) {
        this.$toast.danger(
          "O limite de pré-matrículas para esta turma foi atingido. " +
            "Selecione outra turma ou procure a escola."
        );
        return;
      }

      this.loading = true;
      try {
        await this.$services.preMatriculasService.criarPreMatricula(this.form);
        this.$toast.success("Pré-matrícula criada com sucesso! Aguarde contato da escola.");
        this.$router.push({ name: "login" });
      } catch (error) {
        const errorMessage = error?.message || "Erro ao criar pré-matrícula";

        this.$toast.danger(errorMessage);

        await this.carregarDados();
      } finally {
        this.loading = false;
      }
    },
    voltarEtapa() {
      this.etapa = 1;
      this.alunoExiste = false;
      this.possuiMatriculaAtiva = false;
      this.aluno = null;
      this.form.configuracao_id = null;
      this.form.turma_id = null;
      this.form.serie_id = null;
    },
    async onEscolaChange() {
      this.form.turma_id = null;
      this.form.serie_id = null;

      try {
        const turmas = await this.$services.preMatriculasService.listarTurmasPublico();
        this.todasTurmas = turmas;
        this.turmasPorConfiguracao = turmas.reduce((acc, turma) => {
          if (!acc[turma.configuracao_id]) {
            acc[turma.configuracao_id] = [];
          }
          acc[turma.configuracao_id].push(turma);
          return acc;
        }, {});
      } catch (error) {
        // Silencioso - não precisa alertar o usuário
      }
    },
    async onTurmaChange() {
      this.form.serie_id = null;
      this.todasSeries = [];

      if (!this.form.turma_id) {
        return;
      }

      try {
        const turmas = await this.$services.preMatriculasService.listarTurmasPublico();
        this.todasTurmas = turmas;
        this.turmasPorConfiguracao = turmas.reduce((acc, turma) => {
          if (!acc[turma.configuracao_id]) {
            acc[turma.configuracao_id] = [];
          }
          acc[turma.configuracao_id].push(turma);
          return acc;
        }, {});

        this.todasSeries = await this.$services.preMatriculasService.listarSeriesPorTurma(
          this.form.turma_id
        );
      } catch (error) {
        this.$toast.danger("Erro ao carregar dados da turma");
      }
    },
    async onEstadoChange() {
      this.form.cidade_nascimento = "";

      if (!this.form.uf_id || !this.estadoSelecionado) {
        this.cidades = [];
        return;
      }

      const uf = this.estadoSelecionado.sigla;

      if (this.cidadesCache[uf]) {
        this.cidades = this.cidadesCache[uf];
        return;
      }

      if (this.abortController) {
        this.abortController.abort();
      }

      clearTimeout(this.fetchTimeout);
      this.fetchTimeout = setTimeout(async () => {
        this.loadingCidades = true;
        this.abortController = new AbortController();

        try {
          const response = await fetch(
            `https://servicodados.ibge.gov.br/api/v1/localidades/estados/${uf}/municipios`,
            { signal: this.abortController.signal }
          );
          const municipios = await response.json();
          const cidadesOrdenadas = municipios.map((m) => m.nome).sort();

          this.cidadesCache[uf] = cidadesOrdenadas;
          this.cidades = cidadesOrdenadas;
        } catch (error) {
          if (error.name !== "AbortError") {
            this.$toast.danger("Erro ao carregar cidades");
          }
        } finally {
          this.loadingCidades = false;
          this.abortController = null;
        }
      }, 300);
    },
    formatarDescricaoTurma(turma) {
      if (!turma.limite_prematricula) {
        return turma.descricao;
      }

      if (turma.limite_atingido) {
        return `${turma.descricao} - LOTADA`;
      }

      if (turma.vagas_disponiveis !== null && turma.vagas_disponiveis <= 5) {
        return `${turma.descricao} (${turma.vagas_disponiveis} vagas)`;
      }

      return turma.descricao;
    },
    async carregarDados() {
      this.loading = true;
      try {
        const [escolas, turmas, sexos, estados] = await Promise.all([
          this.$services.preMatriculasService.listarEscolasPublico(),
          this.$services.preMatriculasService.listarTurmasPublico(),
          this.$services.preMatriculasService.listarSexosPublico(),
          this.$services.preMatriculasService.listarEstadosPublico(),
        ]);
        this.escolas = escolas;
        this.todasTurmas = turmas;
        this.sexos = sexos;
        this.estados = estados;
        this.turmasPorConfiguracao = turmas.reduce((acc, turma) => {
          if (!acc[turma.configuracao_id]) {
            acc[turma.configuracao_id] = [];
          }
          acc[turma.configuracao_id].push(turma);
          return acc;
        }, {});
      } catch (error) {
        this.$toast.danger("Erro ao carregar dados");
      } finally {
        this.loading = false;
      }
    },
  },
  mounted() {
    this.carregarDados();
  },
};
</script>

<style scoped>
/* Base Styles */
.pre-matricula-page {
  min-height: 100vh;
  background: linear-gradient(135deg, #a9d9b5 0%, #87bf4e 35%, #51a657 70%, #51a657 100%);
  position: relative;
  overflow-x: hidden;
}

.page-header {
  background: linear-gradient(135deg, #a9d9b5 0%, #87bf4e 35%, #51a657 70%, #51a657 100%);
  padding: 2rem 1rem;
  text-align: center;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
}

.header-content {
  max-width: 600px;
  margin: 0 auto;
}

.form-container {
  position: relative;
  z-index: 2;
  margin-top: -2rem;
}

.form-card {
  border-radius: 8px !important;
  overflow: hidden;
  backdrop-filter: blur(10px);
  background: #ffffff !important;
  animation: slideInUp 0.5s ease;
}

.stepper-container {
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
}

.stepper-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  max-width: 400px;
  margin: 0 auto;
}

.step-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  flex: 1;
}

.step-circle {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: #e0e0e0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  color: #9e9e9e;
  transition: all 0.3s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.step-item.active .step-circle {
  background: linear-gradient(135deg, #87bf4e 0%, #51a657 100%);
  color: white;
  transform: scale(1.1);
  box-shadow: 0 4px 12px rgba(81, 166, 87, 0.4);
}

.step-item.completed .step-circle {
  background: #4caf50;
  color: white;
}

.step-number {
  font-size: 18px;
  font-weight: bold;
}

.step-label {
  font-size: 12px;
  font-weight: 600;
  color: #757575;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.step-item.active .step-label {
  color: #51a657;
}

.step-line {
  flex: 1;
  height: 4px;
  background: #e0e0e0;
  margin: 0 8px;
  border-radius: 2px;
  transition: all 0.3s ease;
  max-width: 80px;
}

.step-line.active {
  background: linear-gradient(90deg, #87bf4e 0%, #51a657 100%);
}

.etapa-content {
  min-height: 300px;
}

.section-header {
  display: flex;
  align-items: center;
  padding-bottom: 12px;
  border-bottom: 2px solid #e0e0e0;
}

.field-label {
  font-weight: 600;
  color: #424242;
  margin-bottom: 4px;
  font-size: 14px;
}

.cpf-input {
  font-size: 18px !important;
  font-weight: 500;
}

.mobile-alert {
  border-radius: 12px !important;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
}

.v-btn {
  transition: all 0.3s ease !important;
}

.v-btn:hover:not(.v-btn--disabled) {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
}

.v-btn:active:not(.v-btn--disabled) {
  transform: translateY(0);
}

.v-btn--loading {
  pointer-events: none;
}

::-webkit-scrollbar {
  width: 8px;
}

::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 10px;
}

::-webkit-scrollbar-thumb {
  background: linear-gradient(135deg, #87bf4e 0%, #51a657 100%);
  border-radius: 10px;
}

::-webkit-scrollbar-thumb:hover {
  background: linear-gradient(135deg, #a9d9b5 0%, #87bf4e 100%);
}

@keyframes slideInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Responsive: Mobile First Approach */
@media (max-width: 599px) {
  .page-header {
    padding: 1.5rem 1rem;
  }

  .form-container {
    margin-top: -1rem;
    padding: 0 8px !important;
  }

  .form-card {
    border-radius: 12px !important;
    margin: 0 !important;
  }

  .stepper-container {
    padding: 1rem !important;
  }

  .v-card-text {
    padding: 1rem !important;
  }

  .etapa-content {
    min-height: 250px;
  }

  .form-actions {
    justify-content: center;
    margin-top: 1.5rem !important;
  }

  .form-actions .v-btn {
    width: 100%;
    max-width: 100%;
    margin: 4px 0 !important;
  }

  .v-text-field,
  .v-select,
  .v-autocomplete {
    margin-bottom: 8px;
  }

  .v-input {
    margin-bottom: 12px;
  }

  .v-btn {
    min-height: 48px;
  }

  .v-btn--large {
    min-height: 48px !important;
    font-size: 16px !important;
  }

  .v-btn--x-large {
    min-height: 54px !important;
    font-size: 18px !important;
  }
}

@media (max-width: 360px) {
  .page-header h1 {
    font-size: 1.25rem !important;
  }

  .page-header p {
    font-size: 0.8rem;
  }

  .page-header .v-icon {
    font-size: 36px !important;
  }

  .step-circle {
    width: 36px;
    height: 36px;
  }

  .step-number {
    font-size: 14px;
  }

  .step-label {
    font-size: 10px;
  }
}

@media (min-width: 361px) and (max-width: 599px) {
  .page-header h1 {
    font-size: 1.5rem !important;
  }

  .page-header p {
    font-size: 0.875rem;
  }

  .page-header .v-icon {
    font-size: 40px !important;
  }

  .step-circle {
    width: 40px;
    height: 40px;
  }

  .step-number {
    font-size: 16px;
  }

  .step-label {
    font-size: 11px;
  }

  .step-line {
    max-width: 40px;
    margin: 0 4px;
  }

  .section-header h3 {
    font-size: 1.1rem !important;
  }

  .field-label {
    font-size: 13px;
  }

  .mobile-alert {
    font-size: 0.875rem;
  }
}

/* Tablet */
@media (min-width: 600px) {
  .form-container {
    padding: 0 16px !important;
    margin-top: -1.5rem;
  }

  .form-card {
    max-width: 700px;
    margin: 0 auto !important;
  }

  .v-card-text {
    padding: 2rem !important;
  }

  .stepper-container {
    padding: 1.5rem !important;
  }

  .step-circle {
    width: 44px;
    height: 44px;
  }

  .etapa-content {
    min-height: 320px;
  }

  .section-header h3 {
    font-size: 1.15rem !important;
  }

  .form-actions {
    justify-content: flex-end;
  }

  .form-actions .v-btn {
    width: auto;
    min-width: 140px;
  }
}

@media (min-width: 600px) and (max-width: 767px) {
  .page-header h1 {
    font-size: 1.75rem !important;
  }

  .page-header .v-icon {
    font-size: 52px !important;
  }
}

/* Desktop Small */
@media (min-width: 768px) {
  .form-container {
    padding: 0 24px !important;
    margin-top: -2rem;
  }

  .form-card {
    max-width: 800px;
  }

  .v-card-text {
    padding: 2.5rem !important;
  }

  .stepper-container {
    padding: 1.75rem 2rem !important;
  }

  .step-circle {
    width: 46px;
    height: 46px;
  }

  .step-line {
    max-width: 100px;
  }

  .etapa-content {
    min-height: 350px;
  }

  .section-header h3 {
    font-size: 1.2rem !important;
  }

  .field-label {
    font-size: 14.5px;
  }
}

@media (min-width: 768px) and (max-width: 959px) {
  .page-header {
    padding: 2.5rem 1rem;
  }

  .page-header h1 {
    font-size: 2rem !important;
  }

  .page-header .v-icon {
    font-size: 56px !important;
  }
}

@media (min-width: 960px) {
  .page-header {
    padding: 3rem 1rem;
  }

  .form-container {
    margin-top: -2.5rem;
    padding: 0 32px !important;
  }

  .form-card {
    max-width: 850px;
  }

  .v-card-text {
    padding: 3rem !important;
  }

  .stepper-container {
    padding: 2rem 3rem !important;
  }

  .etapa-content {
    min-height: 380px;
  }

  .section-header h3 {
    font-size: 1.25rem !important;
  }
}

@media (min-width: 960px) and (max-width: 1264px) {
  .page-header h1 {
    font-size: 2.125rem !important;
  }

  .page-header .v-icon {
    font-size: 60px !important;
  }
}

@media (min-width: 1265px) {
  .page-header {
    padding: 3.5rem 1rem;
  }

  .page-header h1 {
    font-size: 2.25rem !important;
  }

  .form-container {
    margin-top: -3rem;
  }

  .form-card {
    max-width: 900px;
  }

  .v-card-text {
    padding: 3.5rem 4rem !important;
  }

  .stepper-container {
    padding: 2rem 4rem !important;
  }

  .etapa-content {
    min-height: 400px;
  }
}

/* Desktop XL */
@media (min-width: 1905px) {
  .page-header {
    padding: 4rem 1rem;
  }

  .page-header h1 {
    font-size: 2.5rem !important;
  }

  .page-header .v-icon {
    font-size: 72px !important;
  }

  .form-container {
    margin-top: -3.5rem;
  }

  .form-card {
    max-width: 1000px;
  }

  .v-card-text {
    padding: 4rem 5rem !important;
  }

  .stepper-container {
    padding: 2.5rem 5rem !important;
  }

  .step-circle {
    width: 52px;
    height: 52px;
  }

  .step-number {
    font-size: 20px;
  }

  .step-label {
    font-size: 13px;
  }

  .step-line {
    max-width: 120px;
  }

  .etapa-content {
    min-height: 450px;
  }

  .section-header h3 {
    font-size: 1.35rem !important;
  }

  .field-label {
    font-size: 15px;
  }
}
</style>
